import * as yup from 'yup';
import { get } from 'lodash';

import { OutcomeTypeList, ProductTypeList, SourceOfComplaintList } from './enum';
import { validateDateSchema, validateDecimalSchema, validateTextSchema } from 'utils/commonSchema';

yup.setLocale({
  mixed: {
    oneOf: '',
    required: '',
  },
});

const textFieldMax = 4000;
const textFieldMin = 1;

export const maxFileSizeMB = typeof window._CMS_ === 'object' ? window._CMS_.MAX_FILE_UPLOAD_SIZE_IN_MB : 0;
export const fileTypeMatch = (attachments: string[]) => new RegExp('\\.(' + attachments.join('|') + ')$', 'i');

const currencySchema = validateDecimalSchema({
  errorMessage: 'Please enter a dollar figure',
});

interface SchemaProps {
  maxFileSize: number;
}

const validationSchema = ({ maxFileSize }: SchemaProps) =>
  yup.object().shape({
    complaintDate: validateDateSchema({ hasMinDate: false }),

    acknowledgement: yup.string().oneOf(['yes', 'no']),

    acknowledgementDate: yup.date().when('acknowledgement', {
      is: 'yes',
      then: () => validateDateSchema({ hasMinDate: false }),
      otherwise: () => yup.date().nullable(),
    }),

    sourceOfComplaint: yup.string().oneOf(SourceOfComplaintList).required(),

    productType: yup.string().oneOf(ProductTypeList).required(),

    compDescription: validateTextSchema({ minLen: textFieldMin, maxLen: textFieldMax, required: true }),

    compType: yup.string().oneOf(['sales', 'claims', 'financialHardshipApp']).required(),

    resolved: yup.string().oneOf(['yes', 'no']),

    resolvedDate: yup.date().when('resolved', {
      is: 'yes',
      then: () => validateDateSchema({ hasMinDate: false }),
      otherwise: () => yup.date().nullable(),
    }),

    sensitiveCase: yup.string().oneOf(['yes', 'no']),

    outcomeType: yup.string().when('resolved', {
      is: 'yes',
      then: () => yup.string().oneOf(OutcomeTypeList).required(),
      otherwise: () => yup.string(),
    }),

    outcomeDesc: yup.string().when('resolved', {
      is: 'yes',
      then: () => validateTextSchema({ minLen: textFieldMin, maxLen: textFieldMax, required: true }),
      otherwise: () => yup.string(),
    }),

    desiredOutcome: yup.string().when('resolved', {
      is: 'no',
      then: () => validateTextSchema({ minLen: textFieldMin, maxLen: textFieldMax, required: true }),
      otherwise: () => yup.string(),
    }),

    compCategory: yup.array().min(1).max(2).required('Please select 2 categories'),

    amountInDispute: currencySchema,
    amountCompResolved: currencySchema,

    files: yup
      .mixed()
      .nullable()
      .test(
        'fileSize',
        `Maximum file size limit of ${maxFileSize}MB has been exceeded, please remove some files.`,
        (files = []) => {
          const totalFileSize = (files as Array<File>).reduce((prev: number, current: File) => prev + current.size, 0);
          return totalFileSize === 0 || totalFileSize <= maxFileSize * 1024 * 1024;
        }
      )
      .test(
        'fileType',
        `This file type is not allowed, please attach a different document type`,
        function (files = []) {
          const fileNameData: string[] = (files as Array<File>).map((file: File) => file.name);
          const allowedFileTypes: string[] = get(window, '_CMS_.ALLOWED_FILE_EXTENSIONS', []);

          let count = 0;

          fileNameData.forEach((value) => {
            const schema = yup.string().matches(fileTypeMatch(allowedFileTypes), 'Invalid file type');
            if (!schema.isValidSync(value)) count++;
            return schema.isValidSync(value);
          });

          return count === 0;
        }
      ),
  });

export default validationSchema;
